import * as React from 'react';
import cx from 'clsx';
import { Button, Icon } from '@vechaiui/react';
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid';
import { Listbox as ListboxPrimitive, Transition } from '@headlessui/react';
import { groupBy as _groupBy } from 'lodash/fp';

const ListboxRoot = <T, V>({
  children,
  displayValue,
  filterItem,
  getKey,
  getValue,
  groupBy,
  invalid,
  isLoading,
  items: _items,
  onChange,
  placeholder,
  size,
  value,
  ...props
}: ListboxProps<T, V>) => {
  const items = _items.filter((item) => filterItem?.(item) ?? true);
  const getItem = React.useCallback(
    (_value: V) => items.find((item) => getKey(getValue(item)) === getKey(_value)),
    [getKey, getValue, items],
  );
  const [selectedItem, setSelectedItem] = React.useState(value ? getItem(value) : undefined);
  const grouppedItems = groupBy ? _groupBy(groupBy)(items) : undefined;

  const handleChange = (_value: T) => {
    setSelectedItem(_value);
    onChange?.(getValue(_value));
  };

  React.useEffect(() => {
    if (
      !isLoading &&
      !!value &&
      (!selectedItem || value !== getKey(getValue(selectedItem)) || items.length === 0)
    ) {
      setSelectedItem(getItem(value));
    }
  }, [value, items, isLoading, selectedItem, getItem, getKey, getValue]);

  return (
    <ListboxPrimitive {...props} onChange={handleChange} value={selectedItem}>
      {({ open }) => (
        <div className="relative">
          <ListboxPrimitive.Button
            as={Button}
            size={size}
            color={invalid ? 'red' : undefined}
            className={cx(
              'form-field form-field-outline justify-start',
              invalid && 'ring-red-600 ring-1',
            )}
          >
            {!selectedItem && <span className="text-neutral-400 font-normal">{placeholder}</span>}
            {selectedItem && (
              <span className="text-neutral-900 font-normal">{displayValue(selectedItem)}</span>
            )}
            <span className="absolute flex items-center right-2">
              <Icon
                as={SelectorIcon}
                className={cx('w-4 h-4', open ? 'text-primary-500' : 'text-neutral-400')}
                label="selector"
                aria-hidden="true"
              />
            </span>
          </ListboxPrimitive.Button>

          <Transition
            enter="transition ease-in-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-out duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={open}
          >
            <ListboxPrimitive.Options
              static
              className={cx(
                'absolute left-0 z-40 max-h-64 w-full mt-2 rounded-base shadow-sm bg-white border border-neutral-200 p-1 space-y-1 overflow-auto',
              )}
            >
              {grouppedItems &&
                Object.entries(grouppedItems).map(([groupName, gItems]) => (
                  <React.Fragment key={groupName}>
                    <div className="px-4 py-1.5 bg-neutral-50 rounded-base text-neutral-600 font-medium uppercase text-sm">
                      {groupName}
                    </div>
                    {gItems.map(children)}
                  </React.Fragment>
                ))}
              {!grouppedItems && items.map(children)}
            </ListboxPrimitive.Options>
          </Transition>
        </div>
      )}
    </ListboxPrimitive>
  );
};
export type ListboxProps<T, V> = {
  children: (item: T, index: number) => React.ReactNode;
  disabled?: boolean;
  displayValue: (item: T) => string;
  filterItem?: (item: T) => boolean;
  getKey: (item: V) => string;
  getValue: (item: T) => V;
  groupBy?: (item: T) => string;
  invalid?: boolean;
  isLoading?: boolean;
  items: T[];
  name?: string;
  onChange?: (value: V) => void;
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  value?: V;
};

const ListboxOption: React.FC<ListboxOptionProps> = ({ children, ...props }) => {
  return (
    <ListboxPrimitive.Option className="relative" {...props}>
      {({ active, selected, disabled }) => (
        <button
          aria-disabled={disabled}
          className={cx(
            'flex items-center w-full px-4 pl-10 py-2 text-left cursor-base rounded-base',
            active && 'bg-neutral-100',
            selected && 'bg-primary-50 text-primary-800',
          )}
          disabled={disabled}
          type="button"
        >
          <span className={cx('flex-1 block', selected && 'font-bold')}>{children}</span>
          {selected && (
            <span className="absolute inset-y-0 left-0 flex items-center justify-center w-10">
              <Icon as={CheckIcon} label="check" className="w-5 h-5 text-primary-500" />
            </span>
          )}
        </button>
      )}
    </ListboxPrimitive.Option>
  );
};
export type ListboxOptionProps<T = any> = {
  children?: React.ReactNode;
  disabled?: boolean;
  value: T;
};

export default Object.assign(ListboxRoot, { Option: ListboxOption });
